const AUTH_TOKEN_KEY = 'vrp_token';
const AUTH_USER_KEY = 'vrp_user';
const AUTH_PERSIST_KEY = 'vrp_session_persist';
const PENDING_TWO_FACTOR_KEY = 'vrp_pending_2fa';
const PENDING_SESSION_REAUTH_KEY = 'vrp_pending_session_reauth';
const LOGOUT_SUPPRESS_REAUTH_KEY = 'vrp_logout_suppress_session_reauth';

const STORAGE_SCOPE = {
  SESSION: 'sessionStorage',
  LOCAL: 'localStorage'
};

function getStorage(scope) {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window[scope] || null;
  } catch {
    return null;
  }
}

function safeGetItem(storage, key) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSetItem(storage, key, value) {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {}
}

function safeRemoveItem(storage, key) {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {}
}

function readJson(value) {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function readPersistFlagFromStorage(storage) {
  return safeGetItem(storage, AUTH_PERSIST_KEY) === '1';
}

function readStoredValue(key, { preferSession = true } = {}) {
  const sessionStorageRef = getStorage(STORAGE_SCOPE.SESSION);
  const localStorageRef = getStorage(STORAGE_SCOPE.LOCAL);

  const firstStorage = preferSession ? sessionStorageRef : localStorageRef;
  const secondStorage = preferSession ? localStorageRef : sessionStorageRef;

  const firstValue = safeGetItem(firstStorage, key);
  if (firstValue !== null) {
    return firstValue;
  }

  return safeGetItem(secondStorage, key);
}

function writeTransientValue(key, value) {
  const sessionStorageRef = getStorage(STORAGE_SCOPE.SESSION);
  const localStorageRef = getStorage(STORAGE_SCOPE.LOCAL);

  if (value) {
    safeSetItem(sessionStorageRef, key, value);
    safeRemoveItem(localStorageRef, key);
    return;
  }

  safeRemoveItem(sessionStorageRef, key);
  safeRemoveItem(localStorageRef, key);
}

function clearStoredValue(key) {
  writeTransientValue(key, null);
}

function readStoredAuthToken({ preferSession = true } = {}) {
  return readStoredValue(AUTH_TOKEN_KEY, { preferSession });
}

function readStoredAuthUser({ preferSession = true } = {}) {
  const raw = readStoredValue(AUTH_USER_KEY, { preferSession });
  return readJson(raw);
}

function normalizePersistPreference(persistSession = false) {
  return Boolean(persistSession);
}

function clearAuthSession() {
  const sessionStorageRef = getStorage(STORAGE_SCOPE.SESSION);
  const localStorageRef = getStorage(STORAGE_SCOPE.LOCAL);

  [sessionStorageRef, localStorageRef].forEach((storage) => {
    safeRemoveItem(storage, AUTH_TOKEN_KEY);
    safeRemoveItem(storage, AUTH_USER_KEY);
    safeRemoveItem(storage, AUTH_PERSIST_KEY);
  });
}

function setAuthSession({ token, user, persistSession = false }) {
  const sessionStorageRef = getStorage(STORAGE_SCOPE.SESSION);
  const localStorageRef = getStorage(STORAGE_SCOPE.LOCAL);
  const normalizedPersistSession = normalizePersistPreference(persistSession);
  const serializedUser = user === undefined || user === null ? null : JSON.stringify(user);

  if (token) {
    safeSetItem(sessionStorageRef, AUTH_TOKEN_KEY, token);
    if (serializedUser) {
      safeSetItem(sessionStorageRef, AUTH_USER_KEY, serializedUser);
    } else {
      safeRemoveItem(sessionStorageRef, AUTH_USER_KEY);
    }
    safeSetItem(sessionStorageRef, AUTH_PERSIST_KEY, normalizedPersistSession ? '1' : '0');

    if (normalizedPersistSession) {
      safeSetItem(localStorageRef, AUTH_TOKEN_KEY, token);
      if (serializedUser) {
        safeSetItem(localStorageRef, AUTH_USER_KEY, serializedUser);
      } else {
        safeRemoveItem(localStorageRef, AUTH_USER_KEY);
      }
      safeSetItem(localStorageRef, AUTH_PERSIST_KEY, '1');
    } else {
      safeRemoveItem(localStorageRef, AUTH_TOKEN_KEY);
      safeRemoveItem(localStorageRef, AUTH_USER_KEY);
      safeRemoveItem(localStorageRef, AUTH_PERSIST_KEY);
    }
    return;
  }

  clearAuthSession();
}

function readStoredAuthSession() {
  const sessionStorageRef = getStorage(STORAGE_SCOPE.SESSION);
  const localStorageRef = getStorage(STORAGE_SCOPE.LOCAL);

  const sessionToken = safeGetItem(sessionStorageRef, AUTH_TOKEN_KEY);
  const sessionUser = readJson(safeGetItem(sessionStorageRef, AUTH_USER_KEY));
  const sessionPersist = readPersistFlagFromStorage(sessionStorageRef);
  const localToken = safeGetItem(localStorageRef, AUTH_TOKEN_KEY);
  const localUserRaw = safeGetItem(localStorageRef, AUTH_USER_KEY);
  const localUser = readJson(localUserRaw);
  const localPersist = readPersistFlagFromStorage(localStorageRef);

  if (sessionToken) {
    if (localPersist && !sessionPersist) {
      safeSetItem(sessionStorageRef, AUTH_PERSIST_KEY, '1');
    }

    if (localPersist && !sessionUser && localUserRaw) {
      safeSetItem(sessionStorageRef, AUTH_USER_KEY, localUserRaw);
    }

    if (!localPersist && localToken) {
      safeRemoveItem(localStorageRef, AUTH_TOKEN_KEY);
      safeRemoveItem(localStorageRef, AUTH_USER_KEY);
      safeRemoveItem(localStorageRef, AUTH_PERSIST_KEY);
    }

    return {
      token: sessionToken,
      user: sessionUser,
      persistSession: sessionPersist || localPersist,
      source: 'sessionStorage'
    };
  }

  if (!localToken) {
    return null;
  }

  safeSetItem(sessionStorageRef, AUTH_TOKEN_KEY, localToken);
  if (localUserRaw) {
    safeSetItem(sessionStorageRef, AUTH_USER_KEY, localUserRaw);
  } else {
    safeRemoveItem(sessionStorageRef, AUTH_USER_KEY);
  }

  if (localPersist) {
    safeSetItem(sessionStorageRef, AUTH_PERSIST_KEY, '1');
    return {
      token: localToken,
      user: localUser,
      persistSession: true,
      source: 'localStorage'
    };
  }

  safeSetItem(sessionStorageRef, AUTH_PERSIST_KEY, '0');
  safeRemoveItem(localStorageRef, AUTH_TOKEN_KEY);
  safeRemoveItem(localStorageRef, AUTH_USER_KEY);
  safeRemoveItem(localStorageRef, AUTH_PERSIST_KEY);

  return {
    token: localToken,
    user: localUser,
    persistSession: false,
    source: 'localStorage'
  };
}

function buildAuthHeaders(extraHeaders = {}) {
  const token = readStoredAuthToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders
  };
}

function readPendingToken(key) {
  return readStoredValue(key);
}

function setPendingToken(key, token) {
  writeTransientValue(key, token);
}

function clearPendingToken(key) {
  clearStoredValue(key);
}

function hasPendingSessionReauth() {
  return Boolean(readPendingToken(PENDING_SESSION_REAUTH_KEY));
}

function isLogoutSuppressingReauth() {
  return Boolean(readPendingToken(LOGOUT_SUPPRESS_REAUTH_KEY));
}

function setLogoutSuppressReauth(enabled) {
  writeTransientValue(LOGOUT_SUPPRESS_REAUTH_KEY, enabled ? '1' : null);
}

function readPendingTwoFactorToken() {
  return readPendingToken(PENDING_TWO_FACTOR_KEY);
}

function setPendingTwoFactorToken(token) {
  setPendingToken(PENDING_TWO_FACTOR_KEY, token);
}

function clearPendingTwoFactorToken() {
  clearPendingToken(PENDING_TWO_FACTOR_KEY);
}

function readSessionReauthToken() {
  return readPendingToken(PENDING_SESSION_REAUTH_KEY);
}

function setSessionReauthToken(token) {
  setPendingToken(PENDING_SESSION_REAUTH_KEY, token);
}

function clearSessionReauthToken() {
  clearPendingToken(PENDING_SESSION_REAUTH_KEY);
}

function clearPendingAuthState() {
  clearPendingTwoFactorToken();
  clearSessionReauthToken();
}

function dispatchSessionRestoredEvent() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('vrp:session-restored'));
  } catch {}
}

function dispatchSessionExpiredEvent() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('vrp:session-expired'));
  } catch {}
}

export const AUTH_STORAGE_KEYS = {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  AUTH_PERSIST_KEY,
  PENDING_TWO_FACTOR_KEY,
  PENDING_SESSION_REAUTH_KEY,
  LOGOUT_SUPPRESS_REAUTH_KEY
};

export {
  clearAuthSession,
  clearPendingAuthState,
  clearPendingTwoFactorToken,
  clearSessionReauthToken,
  buildAuthHeaders,
  dispatchSessionExpiredEvent,
  dispatchSessionRestoredEvent,
  hasPendingSessionReauth,
  isLogoutSuppressingReauth,
  readPendingTwoFactorToken,
  readSessionReauthToken,
  readStoredAuthSession,
  readStoredAuthToken,
  readStoredAuthUser,
  setAuthSession,
  setLogoutSuppressReauth,
  setPendingTwoFactorToken,
  setSessionReauthToken
};
